import React, { useState, useEffect, useRef } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";

function DentalNews() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [newsTitles, setNewsTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = useRef(null);

  // Backend API base URL
  const API_BASE_URL = "https://backend.dlapakistan.com/api";

  // Utility function to extract data from different API response structures
  const extractDataFromResponse = (response) => {
    if (!response || !response.data) return [];
    
    const data = response.data;
    
    if (Array.isArray(data)) {
      return data;
    } else if (data.results && Array.isArray(data.results)) {
      return data.results;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else if (data.items && Array.isArray(data.items)) {
      return data.items;
    } else {
      console.warn("Unexpected API response structure:", data);
      return [];
    }
  };

  useEffect(() => {
    // Fetch news titles from backend
    const fetchNewsTitles = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/dental-news/`, {
          params: {
            is_published: 'true',
          }
        });
        
        console.log("News API Response:", response.data);
        
        // Extract data using utility function
        const newsData = extractDataFromResponse(response);
        
        // Sort by created_at date (oldest first)
        const sortedNews = [...newsData].sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        );
        
        setNewsTitles(sortedNews);
        setError(null);
      } catch (err) {
        console.error("Error fetching news titles:", err);
        setError("Failed to load news titles. Please try again later.");
        setNewsTitles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsTitles();
  }, []);

  useEffect(() => {
    // Header height detection
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        // Fallback height
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else if (windowWidth < 1024) {
          setHeaderHeight(112);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    // Initial setup
    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    // Resize observer for header changes
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const handleTitleClick = (newsId) => {
    navigate(`/dental/${newsId}/posts`);
  };

  const isMobileView = windowWidth <= 1024;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Dental News
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                Dental News
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#70B444]"></div>
                <p className="mt-4 text-gray-600">Loading dental news...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-2 text-sm underline hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            )}

            {/* News Titles List */}
            {!loading && !error && newsTitles.length > 0 && (
              <div className="space-y-3 md:space-y-4">
                {newsTitles.map((news) => (
                  <div 
                    key={news.id}
                    className="group cursor-pointer transition-all duration-200"
                    onClick={() => handleTitleClick(news.id)}
                  >
                    <div className="p-4 md:p-5 hover:border-[#70B444] hover:shadow-md hover:bg-gray-50 transition-all duration-300">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <h2 className="text-lg sm:text-xl md:text-2xl text-[#70B444] group-hover:text-[#538630] font-semibold mb-2">
                            {news.title}
                          </h2>
                          
                          {/* Post count badge */}
                          {news.posts_count > 0 && (
                            <div className="flex items-center text-sm text-gray-500">
                              <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                                {news.posts_count} {news.posts_count === 1 ? 'post' : 'posts'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 text-[#70B444] opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && newsTitles.length === 0 && (
              <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-5xl mb-4 text-gray-400">📰</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Dental News Available</h3>
                <p className="text-gray-600 mb-4">There are no dental news articles published yet.</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="text-[#70B444] underline hover:no-underline text-sm"
                >
                  Refresh page
                </button>
              </div>
            )}
          </div>

          {/* Overlay */}
          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default DentalNews;